import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useGlobalState } from "../../utilities/hooks/use-global-state";
import { AnswerFormats } from "../../models/enumerations/answer-formats";
import { CategoryIds } from "../../models/enumerations/category-ids";
import { GroupIds } from "../../models/enumerations/group-ids";
import { SlideType } from "../../models/enumerations/slide-types";
import AssessmentSettings from "../../models/interfaces/assessment-settings";
import { Question } from "../../models/interfaces/questions/question";
import { LifestyleAssessmentResponse } from "../../models/interfaces/scores/lifestyle-assessment-response";
import { LifestyleScore } from "../../models/interfaces/scores/lifestyle-score";
import { MskScore } from "../../models/interfaces/scores/msk-score";
import { UserInfo } from "../../models/interfaces/scores/user-info";
import { AssessmentUtils } from "../../utilities/assessments/assessment-utils";
import usePrevious from "../../utilities/hooks/use-previous";
import MskScoreUtil from "../../utilities/msk-score-util";
import UserMskFocusArticlesUtil from "../../utilities/user-msk-focus-articles-util";
import UserUtilAssessments from "../../utilities/user-util-assessments";
import AppModal from "../app-modal/app-modal";
import LoaderAssessments from "../loader-assessments/loader-assessments";
import { createLogger } from "../../utilities/logger";
import ScreenSlide from "../screen-slide/screen-slide";
import { useStoreAssessments } from "../../store/useStoreAssessments";
// import TodayService from "../../../utilities/services/today-service/today-service";
import { ActivityType } from "../../models/enumerations/activity-type";
// import { sadSharp } from "ionicons/icons";
// import IToday from "../../../models/interfaces/today";
// import { LocalStorageUtils } from "../../../utilities/local-storage-utils";
import UserActivityService from "../../utilities/services/user-activity-service";
import { DateTime } from "luxon";
import UserActivity from "../../models/interfaces/user-activity";
import UserService from "../../utilities/services/user-service";
import ReportService from "../../utilities/services/report-service-assessments/report-service-assessments";
import { ReportType } from "../../models/enumerations/report-type";
import { Report } from "../../models/interfaces/report";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import BaseCardLayoutActions from "../base-card-layout/base-card-layout-actions";
import { useIntercom } from "react-use-intercom";
import { CorrectiveProgramUtils } from "../../utilities/corrective-program-utils";
import { WellnessProgram } from "../../models/interfaces/wellness-program";
import ArticleUtils from "../../utilities/article-utils";
import WellnessProgramService from "../../utilities/services/wellness-program-service";
import { SymmioAccessType } from "../../models/enumerations/symmio-access-type";
import UserCorrectiveProgramsService from "../../utilities/services/corrective-programs-service/user-corrective-programs-service";
import UserMskFocusArticlesService from "../../utilities/services/article-service/user-msk-articles-service";
import { useTranslation } from 'react-i18next';
import { useNavigationDirection } from "../navigation-direction-context/navigation-direction-content";
import { enqueueSnackbar } from "notistack";
import i18n from "../../i18n";
import { scheduleAiMskScoreSummary } from "../../utilities/ai-response-utils";

const log = createLogger("LifestyleScreen");

const COMPONENT_CLASS = "c-movement-screen";

export interface LifestyleScreenProps {
    screenSettings: AssessmentSettings;
    setScreenSettings: React.Dispatch<AssessmentSettings>;
    settingsOnBack?: Function;
    onSubmit?: () => Promise<void>;
    groupId?: string;
}

const LifestyleScreen: React.FC<LifestyleScreenProps> = (props) => {
    const { setDirection } = useNavigationDirection();
    const { screenSettings, setScreenSettings, onSubmit: onSubmitCallback, groupId: groupIdProp } = props;
    const { trackEvent } = useIntercom();
    const { setGlobalError, setHasMskScore } = useGlobalState();
    const { state, setState } = useAuthState();
    const history = useHistory();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isLoadingNextGroup, setLoadingNextGroup] = useState(false);
    const [lifestyleScore, setLifestyleScore] = useState<
        LifestyleScore | undefined | null
    >(undefined);
    const [errors, setErrors] = useState<string[]>([]);
    const [errorIntro, setErrorIntro] = useState<string | undefined>();
    const [mskScore, setMskScore] = useState<MskScore>();
    const [userInfo, setUserInfo] = useState<UserInfo>();
    const [sendTrackEventFirstScore, setSendTrackEventFirstScore] = useState<boolean>(false);
    // const [today, setToday] = useState<IToday>();
    const previousGroupIndex = usePrevious<number>(
        screenSettings.currentGroupIndex
    );
    const retryMs: number[] = [500, 1000, 1500, 3000, 5000];
    const { t } = useTranslation();
    const setLastActivityCompletedDate = useStoreAssessments(
        (state) => state.setLastActivityCompleted
    );
    useEffect(() => {
        try {
            if (!state.user || !state.user.id) {
                new Error("Please log in.")
                return;
            }
            const userId = state.user.id;
            MskScoreUtil.getLatest(userId).then((mskScore) => {
                if (mskScore && mskScore.percentage == null) {
                    setState((state) => ({
                        ...state, ...{ temp: { baselineAssessment: true } }
                    }));
                } else {
                    setState((state) => ({
                        ...state, ...{ temp: { baselineAssessment: false } }
                    }));
                }
                setMskScore(mskScore);
            });
            UserUtilAssessments.getUserInfo(userId).then((userInfo) => {
                if (!userInfo) {
                    // they don't have bioSex or birthdate. Take them to setup screen
                    history.replace("/setup");
                }
                setUserInfo(userInfo);
            });

            const params = new URLSearchParams(window.location.search);
            const groupId = groupIdProp || params.get("groupId");
            AssessmentUtils.fetchQuestions(
                screenSettings,
                CategoryIds.LIFESTYLE,
                groupId
            ).then((settings) => {
                settings.specificGroupId = groupId;
                setScreenSettings(settings);
                setIsLoading(false);
            }).catch((e) => {
                log.error("[LifestyleScreen] fetchQuestions error", {
                    exception: e,
                });
            });
        } catch (e) {
            log.error("[LifestyleScreen] useEffect error", {
                exception: e,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const shouldScore = AssessmentUtils.shouldScoreGroup(
            screenSettings,
            previousGroupIndex,
            screenSettings.currentGroupIndex
        );
        if (!shouldScore) {
            return;
        }

        setErrors([]);
        setIsLoading(true);
        scoreGroup();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screenSettings.currentGroupIndex]);

    const scoreGroup = async (attemptIndex: number = 0): Promise<void> => {
        if (!userInfo) {
            // they don't have bioSex or birthdate. Take them to setup screen
            history.replace("/setup");
            return;
        }
        let assessmentResponse: LifestyleAssessmentResponse | undefined =
            undefined;
        try {
            assessmentResponse = await AssessmentUtils.scoreGroup(
                userInfo || {},
                screenSettings,
                previousGroupIndex,
                mskScore,
                undefined
            );
        } catch (err: any) {
            if (attemptIndex === 0) {
                setGlobalError("Lifestyle: Calculate", err);
            }
            if (attemptIndex < retryMs.length) {
                setTimeout(() => {
                    return scoreGroup(attemptIndex + 1);
                }, retryMs[attemptIndex]);
                return;
            } else {
                log.error("Failed to score lifestyle group after retries", {
                    exception: err,
                    groupId: previousGroupIndex,
                    attempts: attemptIndex + 1,
                });
                await handleError([
                    t('assessments.err_notScored'),
                ]);
                return;
            }
        }

        // error handling
        if (assessmentResponse == null) {
            log.error("Lifestyle group scoring returned no result", {
                groupId: previousGroupIndex,
            });
            await handleError([
                t('assessments.err_notCalculated'),
            ]);
            return;
        }
        if (
            assessmentResponse.errors != null &&
            assessmentResponse.errors.length >= 1
        ) {
            log.error("Lifestyle group scored with errors", {
                errors: assessmentResponse.errors,
                groupId: previousGroupIndex,
            });
            await handleError(
                assessmentResponse.errors,
                t('assessments.err_hasErrors'),
            );
            return;
        }

        log.info("Lifestyle group scored", {
            groupId: previousGroupIndex,
            mskScoreId: assessmentResponse.mskScore?.id,
        });

        // save group
        saveGroup(assessmentResponse);
    };

    const saveGroup = async (
        lifestyleAssessmentResponse: LifestyleAssessmentResponse,
        attemptIndex: number = 0
    ): Promise<void> => {
        let newMskScore: MskScore | undefined = undefined;
        try {
            if (!state.user || !state.user.id) {
                return;
            }
            newMskScore = await AssessmentUtils.saveGroupByUserId(
                lifestyleAssessmentResponse,
                state.user.id,
                (hasMskScore: boolean) => setHasMskScore(hasMskScore)
            );
        } catch (err: any) {
            if (attemptIndex === 0) {
                setGlobalError("Lifestyle: Save", err);
            }
            if (attemptIndex < retryMs.length) {
                setTimeout(() => {
                    return saveGroup(
                        lifestyleAssessmentResponse,
                        attemptIndex + 1
                    );
                }, retryMs[attemptIndex]);
                return;
            } else {
                log.error("Failed to save lifestyle group after retries", {
                    exception: err,
                    mskScoreId: lifestyleAssessmentResponse.mskScore?.id,
                    attempts: attemptIndex + 1,
                });
                await handleError([
                    t('assessments.err_couldNotBeSaved'),
                ]);
                return;
            }
        }
        if (!mskScore?.percentage && newMskScore?.percentage) {
            setSendTrackEventFirstScore(true);
        }
        // error handling
        if (newMskScore == null) {
            log.error("Lifestyle group save returned no result");
            await handleError([
                t('assessments.err_notSaved'),
            ]);
            return;
        }
        if (newMskScore.errors != null && newMskScore.errors.length >= 1) {
            log.error("Lifestyle group saved with errors", {
                errors: newMskScore.errors,
            });
            await handleError(
                newMskScore.errors,
                t('assessments.err_hasErrors2'),
            );
            return;
        }

        // handle changes
        if (!state.user || !state.user.id) {
            return
        }
        UserMskFocusArticlesUtil.handleFocusChanges(
            state.user.id,
            newMskScore
        ).catch((e) => {
            log.error("Failed to apply focus changes after assessment", {
                exception: e,
            });
        });
        setMskScore(newMskScore);
        setIsLoading(false);
        setErrors([]);
    };

    // NOTE: this throws warnings in the tests for some reason, but works functionally
    const handleError = async (
        errors: string[],
        instructions?: string
    ): Promise<void> => {
        if (errors == null || errors.length === 0) {
            setErrors([]);
            return;
        }

        setScreenSettings(
            await AssessmentUtils.moveToPreviousPage(screenSettings)
        );
        setErrorIntro(instructions);
        setErrors(errors);
        setIsLoading(false);
    };

    const getToggleLoadingNextGroup = (settings: AssessmentSettings) => {
        let toggleLoadingNextGroup = false;
        const isLastSlideInGroup = AssessmentUtils.isLastSlide(settings);
        if (isLastSlideInGroup) {
            const group = settings.slideGroups[settings.currentGroupIndex];
            toggleLoadingNextGroup = group.nextGroupIdDeterminedByApi === true;
        }
        return toggleLoadingNextGroup;
    };

    const getNextPage = async () => {
        const toggleLoadingNextGroup =
            getToggleLoadingNextGroup(screenSettings);
        if (toggleLoadingNextGroup) {
            setLoadingNextGroup(true);
        }
        setDirection('forward');
        setScreenSettings(await AssessmentUtils.moveToNextPage(screenSettings));
        if (toggleLoadingNextGroup) {
            setLoadingNextGroup(false);
        }
    };

    const recordAnswer = async (selectedAnswer: string, question: Question, forceAutoSubmit?: boolean) => {
        let settings = AssessmentUtils.recordAnswer(
            screenSettings,
            question,
            selectedAnswer
        );

        if (
            slide?.slideType === SlideType.Question &&
            slide.questions != null
        ) {
            let autoSubmit = false;
            slide.questions.forEach((question) => {
                if (AssessmentUtils.autoSubmitQuestion(question)) {
                    autoSubmit = true;
                }
            });
            setDirection('forward');
            if (autoSubmit || forceAutoSubmit) {
                const toggleLoadingNextGroup =
                    getToggleLoadingNextGroup(settings);
                if (toggleLoadingNextGroup) {
                    setLoadingNextGroup(true);
                }
                settings = await AssessmentUtils.moveToNextPage(settings);
                if (toggleLoadingNextGroup) {
                    setLoadingNextGroup(false);
                }
            }
        }

        setScreenSettings(settings);
    };

    const handleSubmit = async () => {
        if (onSubmitCallback) {
            await onSubmitCallback();
        } else {
            await submit();
        }
    };

    const submit = async () => {
        const queryParams = new URLSearchParams(window.location.search);

        setIsLoading(true);
        setLifestyleScore(mskScore?.lifestyleScore);
        // already submitted
        setErrors(mskScore?.errors ?? []);
        if (mskScore?.errors && mskScore.errors.length) {
            setIsLoading(false);
        }
        if (queryParams.get("retake") === "true") {
            setLastActivityCompletedDate(
                new Date(),
                ActivityType.FullMskRetakePortal
            );
            const userActivity: UserActivity = {
                date: DateTime.now().toISODate(),
                type: ActivityType.FullMskRetakePortal,
                userId: state.user?.id ?? ""
            }
            await UserActivityService.add(userActivity, state.user).catch((e) => {
                log.error("Failed to record retake activity", {
                    exception: e,
                    activityType: ActivityType.FullMskRetakePortal,
                });
            });
        }
        else {
            if (!state.temp.baselineAssessment) { // This is not a baseline
                const userActivity: UserActivity = {
                    date: DateTime.now().toISODate(),
                    type: ActivityType.WebLifestyleAssessment,
                    userId: state.user?.id ?? ""
                }

                await UserActivityService.add(userActivity, state.user).catch((e) => {
                    log.error("Failed to record lifestyle assessment activity", {
                        exception: e,
                        activityType: ActivityType.WebLifestyleAssessment,
                    });
                });
                setLastActivityCompletedDate(
                    new Date(),
                    ActivityType.LifestyleAssessment
                );
            }
            else { // This is a baseline
                const userActivity: UserActivity = {
                    date: DateTime.now().toISODate(),
                    type: ActivityType.WebBaselineLifestyleAssessment,
                    userId: state.user?.id ?? ""
                }

                await UserActivityService.add(userActivity, state.user).catch((e) => {
                    log.error("Failed to record baseline lifestyle assessment activity", {
                        exception: e,
                        activityType: ActivityType.WebBaselineLifestyleAssessment,
                    });
                });
                setLastActivityCompletedDate(
                    new Date(),
                    ActivityType.WebBaselineLifestyleAssessment
                );
            }
            setState((state) => ({
                ...state, ...{ temp: null }
            }));
        }

        log.info("Lifestyle assessment submitted", {
            baseline: state.temp?.baselineAssessment === true,
            retake: queryParams.get("retake") === "true",
        });

        if (sendTrackEventFirstScore) {
            trackEvent('first-time-assessments-completed');
        }
        // If the user hasn't completed their last assessment
        if (mskScore?.percentage == null) {
            history.push("/wellness-assessment");
        }
        // Else, the user has completed their last assessment
        else {
            // We are creating a CE instead of checking if one exists because after a retest, there must be a new CE routine
            // Logic in today is to create CE and ECE
            // checking mskScore to ensure correctives and articles should be created.
            if (state.user && state.user.id && state.user.symmioAccess === SymmioAccessType.AppLicense && mskScore.lifestyleScore?.percentage != null && mskScore.focusAreas != null &&
                mskScore.focusAreas.length >= 1) {
                let wellnessProgram: WellnessProgram = {
                    createdAtLocal: DateTime.now().toISO(),
                    lengthInDays: 0,
                    focusAreas: undefined,
                    movementFocusAreas: undefined,
                    currentDay: 1,
                    correctiveExerciseId: undefined,
                    focusArticleId: undefined,
                    isCurrent: true,
                    userId: state.user.id,
                    currentProgress: [],
                    numOfAdherenceAccountedFor: 0,
                }
                try {
                // Before we create correctives/articles, we must set any isCurrent = true to false
                const currentCorrectiveExercises = await UserCorrectiveProgramsService.getCurrentByUserId(state.user.id)
                const currentExtraCorrectiveExercises = await UserCorrectiveProgramsService.getCurrentExtraByUserId(state.user.id)
                const currentArticles = await UserMskFocusArticlesService.getAllCurrentByUserId(state.user.id)
                if (currentCorrectiveExercises && currentCorrectiveExercises.length > 0) {
                    for (const correctiveExercise of currentCorrectiveExercises) {
                        correctiveExercise.isCurrent = false;
                        await UserCorrectiveProgramsService.update(correctiveExercise);
                    }
                }
                if (currentExtraCorrectiveExercises && currentExtraCorrectiveExercises.length > 0) {
                    for (const correctiveExercise of currentExtraCorrectiveExercises) {
                        correctiveExercise.isCurrent = false;
                        await UserCorrectiveProgramsService.update(correctiveExercise);
                    }
                }
                if (currentArticles && currentArticles.length > 0) {
                    for (const article of currentArticles) {
                        article.isCurrent = false;
                        await UserMskFocusArticlesService.update(article);
                    }
                }
                // Create correctiveExercise
                // For some reason old team made it so if there is no extra corrective, still create a corrective of 100 days and no routine
                const ce = await CorrectiveProgramUtils._createUserCorrectiveProgram(state.user.id);
                const articles = await ArticleUtils.createUserMskArticles(state.user.id, mskScore)

                if (ce && ce.hasCorrectiveProgram) {
                    wellnessProgram.correctiveExerciseId = ce.userCorrectiveProgram.id;
                    wellnessProgram.lengthInDays = ce.userCorrectiveProgram.routineDays;
                }
                // Create article
                if (articles && articles.id) {
                    wellnessProgram.focusArticleId = articles.id;
                    let longestArticleLength = 0
                    for (let i = 0; i < articles.focusStatuses.length; i++) {
                        let currentCheckArticleLength = 0
                        if (articles.focusStatuses.length === 1) {
                            // User currently cannot get movement_health without a corrective. And the amount of articles
                            // the user gets from movement_health or movement_health_breathing cannot be > length from corrective
                            // Therefore we ignore checking if length is more since it becomes complicated to determine exact 
                            // movement_health articles
                            // IF in the future, movement_health article length can be greater than corrective, we need to change
                            // THIS logic to factor in potential length of numOfArticles from movement_health
                            if (articles.focusStatuses[i].mskFocusAreaGroupId !== GroupIds.MOVEMENT_HEALTH && articles.focusStatuses[i].mskFocusAreaGroupId !== GroupIds.MOVEMENT_HEALTH_BREATHING) {
                                currentCheckArticleLength = (i + 1) + (2 * (articles.focusStatuses[i].numArticles - 1))
                            }
                        }
                        else {
                            if (articles.focusStatuses[i].mskFocusAreaGroupId !== GroupIds.MOVEMENT_HEALTH && articles.focusStatuses[i].mskFocusAreaGroupId !== GroupIds.MOVEMENT_HEALTH_BREATHING) {
                                currentCheckArticleLength = (i + 1) + (articles.focusStatuses.length * (articles.focusStatuses[i].numArticles - 1))
                            }
                        }
                        if (currentCheckArticleLength > longestArticleLength) {
                            longestArticleLength = currentCheckArticleLength;
                        }
                    }
                    if (longestArticleLength > wellnessProgram.lengthInDays) {
                        wellnessProgram.lengthInDays = longestArticleLength
                    }
                }
                if (mskScore.focusAreas) {
                    wellnessProgram.focusAreas = mskScore.focusAreas;
                }
                if (mskScore.movementScore && mskScore.movementScore.focusArea) {
                    wellnessProgram.movementFocusAreas = mskScore.movementScore.focusArea
                }

                // Create wellness Program
                const savedWellnessProgram = await WellnessProgramService.add(wellnessProgram);
                log.info("Wellness program created after assessment", {
                    wellnessProgramId: savedWellnessProgram.id,
                    lengthInDays: savedWellnessProgram.lengthInDays,
                    hasCorrectiveProgram: !!ce?.hasCorrectiveProgram,
                    hasArticles: !!articles?.id,
                });
                state.user.wellnessProgramId = savedWellnessProgram.id
                if (state.user.administeredRetake) {
                    state.user.administeredRetake = false
                }
                const stateUser = state.user
                // updating FB user
                await UserService.updateFields(stateUser, {
                    wellnessProgramId: savedWellnessProgram.id,
                    administeredRetake: state.user.administeredRetake
                });
                // update state
                // Set the state to prepare everything for logging in.
                setState((state) => ({
                    ...state, ...{ user: stateUser }
                }));
                } catch (e) {
                    log.error("Failed to create wellness program after assessment", {
                        exception: e,
                    });
                }
            }

            // create new aiResponse object
            await scheduleAiMskScoreSummary(mskScore, state.user?.organizationId).catch((e) => {
                log.error("Failed to schedule AI MSK score summary", {
                    exception: e,
                });
            });

            handleGenerateReport(true);
            history.push("/wellness-assessment");
        }
    };

    const currentGroup =
        screenSettings?.slideGroups == null
            ? null
            : screenSettings.slideGroups[screenSettings.currentGroupIndex];
    const slide = AssessmentUtils.getSlide(screenSettings);
    const actionsModifier =
        slide?.slideType === SlideType.Video ? "-video" : "";

    let hasContinueButton =
        AssessmentUtils.hasData(screenSettings) &&
        (!AssessmentUtils.isLastGroup(screenSettings) ||
            !AssessmentUtils.isLastSlide(screenSettings));

    if (
        hasContinueButton &&
        slide &&
        slide?.slideType === SlideType.Question &&
        slide.questions != null
    ) {
        slide.questions.forEach((question) => {
            if (AssessmentUtils.autoSubmitQuestion(question)) {
                hasContinueButton = false;
            }
            if (AssessmentUtils.multiQuestion(question)) {
                hasContinueButton = false
            }
        });
    }

    let continueButtonText = t('buttons.btn_continue');;
    if (hasContinueButton) {
        switch (slide?.slideType) {
            case SlideType.AssessmentIntro:
            case SlideType.Tests:
                continueButtonText = t('buttons.btn_letsDoIt');
                break;
            case SlideType.GroupIntro:
                continueButtonText = t('buttons.btn_start');
                break;
            case SlideType.Video:
                const questions = AssessmentUtils.getSlide(
                    props.screenSettings
                )?.questions;
                if (
                    questions != null &&
                    questions.length >= 1 &&
                    questions[0].questionId === "BREATHING_BREATH"
                ) {
                    continueButtonText = t('buttons.btn_goToTimer');
                } else {
                    continueButtonText = t('buttons.btn_enterResults');
                }
                break;
        }
    }

    const hasSubmitButtton =
        AssessmentUtils.isLastGroup(screenSettings) &&
        AssessmentUtils.isLastSlide(screenSettings);

    const noProgressBarSlideTypes = [
        SlideType.AssessmentIntro,
        SlideType.Complete,
        SlideType.GroupIntro,
        SlideType.Tests,
        SlideType.Tips,
    ];
    const hasProgressBar =
        slide == null ||
        noProgressBarSlideTypes.indexOf(slide.slideType) === -1;

    const collapseScrollTypes = [
        AnswerFormats.INTEGER,
        AnswerFormats.TIME,
        AnswerFormats.DECIMAL,
    ];
    const shouldCollapseScroll =
        slide?.slideType === SlideType.Question &&
        slide.questions &&
        slide.questions.filter(
            (q) => collapseScrollTypes.indexOf(q.answerFormat) > -1
        ).length > 0;

    const conditionalGroups = [
        GroupIds.BEHAVIORAL_QUESTIONNAIRE,
        GroupIds.SLEEP_PSQI,
        GroupIds.NUTRITION_QUESTIONNAIRE,
    ];
    const questionGroupNames = AssessmentUtils.getAssessmentGroups(
        screenSettings
    )
        .filter(
            (group) =>
                conditionalGroups.indexOf(
                    GroupIds[group.groupId as keyof typeof GroupIds]
                ) === -1
        )
        .map((group) => group.groupId!);

    const handleGenerateReport = async (emailResults: boolean) => {
        if (!state.user || !state.user.id) return;

        const userInFirebase = await UserService.get(state.user.id);

        if (!userInFirebase || !userInFirebase.organizationId || !userInFirebase.id) return;

        const latestOrgId = userInFirebase.organizationId;


        const date = DateTime.now().toISODate();
        const report: Report = {
            dateEnd: date,
            dateStart: date,
            emailResults: emailResults,
            organizationId: latestOrgId,
            status: "scheduled",
            type: ReportType.Wellness,
            userId: userInFirebase.id,
            mskScoreId: mskScore ? mskScore.id : undefined,
            language: i18n.language,
        };

        const scheduledReport = await ReportService.save(
            report,
            userInFirebase
        ).catch((e) => {
            log.error("Failed to schedule lifestyle report", {
                exception: e,
                organizationId: userInFirebase.organizationId,
            });
            return null;
        });

        if (!scheduledReport || !scheduledReport.id) {
            if (scheduledReport == null) {
                log.error("Lifestyle report was not scheduled", {
                    organizationId: userInFirebase.organizationId,
                });
            } else {
                log.error("Lifestyle report scheduled without an id", {
                    organizationId: userInFirebase.organizationId,
                });
            }
            enqueueSnackbar(t('general.error.reportNotSaved'), { variant: "error" });
            return;
        }

        log.info("Lifestyle report scheduled", {
            reportId: scheduledReport.id,
            mskScoreId: report.mskScoreId,
            emailResults: scheduledReport.emailResults,
        });

        if (scheduledReport.emailResults === true) {
            log.info("Report emailed", {
                reportId: scheduledReport.id,
                emailResults: true,
            });
            enqueueSnackbar(t('pages.lifestyleScreen.emailedReport'), { variant: "toast", width: "600px" });
            return;
        }
    };

    return (
        <div
            className={`${COMPONENT_CLASS} -lifestyle ${shouldCollapseScroll ? "-collapse-scroll" : ""
                }`}>
            <LoaderAssessments
                isLoading={isLoading || isLoadingNextGroup} />
            {
                // if
                !isLoading && currentGroup != null && (
                    <div
                        className={`${COMPONENT_CLASS}__wrapper ${slide?.slideType === SlideType.GroupIntro
                            ? "-intro"
                            : ""
                            }`}>
                        {
                            // if
                            AssessmentUtils.hasData(screenSettings) &&
                            lifestyleScore == null && (
                                <div
                                    className={`${COMPONENT_CLASS}__content ${hasContinueButton ||
                                        hasSubmitButtton
                                        ? "has-button"
                                        : ""
                                        }`}>
                                    <AppModal
                                        isOpen={errors?.length >= 1}
                                        onDismiss={() => {
                                            setErrors([]);
                                        }}>
                                        <div
                                            style={{
                                                padding: "10px",
                                                color: "#990000",
                                            }}>
                                            {
                                                // if
                                                errorIntro != null && (
                                                    <div>
                                                        {errorIntro}
                                                        <br />
                                                        <br />
                                                    </div>
                                                )
                                            }
                                            <div>{errors.join("; ")}</div>
                                        </div>
                                    </AppModal>
                                    {
                                        // if
                                        slide != null && (
                                            <ScreenSlide
                                                hasProgressBar={hasProgressBar}
                                                screenSettings={screenSettings}
                                                questionGroupNames={
                                                    questionGroupNames
                                                }
                                                recordedAnswers={AssessmentUtils.getAllResponses(
                                                    screenSettings
                                                )}
                                                recordAnswer={recordAnswer}
                                                page={slide}
                                                errors={errors}
                                                currentProgress={AssessmentUtils.getProcessCurrent(
                                                    screenSettings
                                                )}
                                                totalProgress={AssessmentUtils.getProcessTotal(
                                                    screenSettings
                                                )}
                                                handleOptionalQuestion={() =>
                                                    getNextPage()
                                                }
                                                settingsOnBack={props.settingsOnBack}
                                                isActionsWithContinue={hasContinueButton}
                                                skipButton={slide?.questions &&
                                                    slide.questions.length >
                                                    0 &&
                                                    slide.questions[0]
                                                        .optional === true}
                                                onSkip={getNextPage}
                                                onBack={props.settingsOnBack}
                                                onSubmit={getNextPage}
                                                submitButtonText={continueButtonText}
                                                headerText={t('assessments.lifestyle')}
                                            />
                                        )
                                    }
                                    <div
                                        className={`${COMPONENT_CLASS}__actions ${actionsModifier}`}>
                                        {/* {
                                            // if
                                            hasContinueButton && (
                                                <BaseCardLayoutActions backButton={true} onBack={props.settingsOnBack} submitButton={true} onSubmit={getNextPage} submitButtonText={continueButtonText} skipButton={slide?.questions &&
                                                    slide.questions.length >
                                                    0 &&
                                                    slide.questions[0]
                                                        .optional === true}
                                                    onSkip={() => {
                                                        getNextPage();
                                                    }}></BaseCardLayoutActions>

                                            )
                                        } */}
                                        {
                                            // if
                                            hasSubmitButtton && (
                                                <div className={`c-screen-slide__actions`}>
                                                    <BaseCardLayoutActions backButton={true} onBack={props.settingsOnBack} submitButton={true} onSubmit={handleSubmit} submitButtonText={errors.length === 0
                                                        ? t('buttons.btn_submitResults')
                                                        : t('buttons.btn_resubmit')}></BaseCardLayoutActions>
                                                </div>
                                                // <IonButton
                                                //     color="primary"
                                                //     expand="block"
                                                //     onClick={() =>
                                                //         submit()
                                                //     }>
                                                //     Submit Results
                                                // </IonButton>
                                            )
                                        }

                                        {/* {
                                            // if
                                            slide?.questions &&
                                            slide.questions.length >
                                            0 &&
                                            slide.questions[0]
                                                .optional === true && (
                                                <IonButton
                                                    size="small"
                                                    fill="clear"
                                                    className={`${COMPONENT_CLASS}__optional-text`}
                                                    onClick={() =>
                                                        getNextPage()
                                                    }>
                                                    Skip, I'll fill it
                                                    in later.
                                                </IonButton>
                                            )
                                        } */}
                                    </div>
                                </div>
                            )
                        }
                    </div>
                )
            }
        </div>
    );
};

export default LifestyleScreen;
